import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import { FormError } from '@/components/field-error'
import { PortalLanguageSetting } from '@/components/portal/portal-language-setting'
import { PortalSetPassword } from '@/components/portal/portal-set-password'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'

/** Mon compte : qui je suis pour cet espace, et l'adresse où les courriels partent. */
export const Route = createFileRoute('/portal/compte/')({
  component: PortalAccount,
})

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <p className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span> <span>{children}</span>
    </p>
  )
}

function usePortalMe() {
  return useQuery({ queryKey: ['portal', 'me'], queryFn: () => portalApi.me() })
}

function PortalAccount() {
  const me = usePortalMe()
  const t = msg().portalAccount

  return (
    <div className="space-y-4">
      <IdentityCard me={me} />
      {me.data && <PortalLanguageSetting language={me.data.language} />}
      {me.data?.has_password === false && <PortalSetPassword />}
      <Card>
        <CardHeader>
          <CardTitle>{t.signedTitle}</CardTitle>
          <CardDescription>
            {t.signedHint}{' '}
            <Link to="/portal/validations" className="font-medium text-primary hover:underline">
              {t.signedLink}
            </Link>
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}

function IdentityCard({ me }: { me: ReturnType<typeof usePortalMe> }) {
  const t = msg().portalAccount
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.identity}</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {me.isPending ? (
          <Skeleton className="h-20 w-full rounded-md" />
        ) : me.isError ? (
          <FormError>{me.error instanceof ApiError ? me.error.message : t.unreadable}</FormError>
        ) : (
          <>
            <Row label={t.name}>{me.data.user.name}</Row>
            <Row label={t.email}>{me.data.user.email}</Row>
            <Row label={t.organisation}>{me.data.client.name}</Row>
          </>
        )}
      </CardContent>
    </Card>
  )
}
